import { Component, type ReactNode, type ErrorInfo } from 'react';
import i18next from 'i18next';
import { reportReactError } from '../api/errorReporting';
import { Button } from './ui';

interface Props {
  children: ReactNode;
  fallback?: ReactNode;
}

interface State {
  hasError: boolean;
  error?: Error;
}

/**
 * Global Error Boundary for catching React rendering errors
 * 
 * Features:
 * - Catches errors in child component tree
 * - Reports errors to backend
 * - Shows friendly fallback UI
 * - Allows retry/recovery
 */
export class ErrorBoundary extends Component<Props, State> {
  constructor(props: Props) {
    super(props);
    this.state = { hasError: false };
  }

  static getDerivedStateFromError(error: Error): State {
    return { hasError: true, error };
  }

  componentDidCatch(error: Error, errorInfo: ErrorInfo) {
    // Report to backend
    reportReactError(error, errorInfo.componentStack ?? undefined);
    
    // Also log to console for debugging
    console.error('[ErrorBoundary] Caught error:', error);
    console.error('[ErrorBoundary] Component stack:', errorInfo.componentStack);
  }

  handleRetry = () => {
    this.setState({ hasError: false, error: undefined });
  };

  handleReload = () => {
    window.location.reload();
  };

  render() {
    if (this.state.hasError) {
      // Custom fallback UI
      if (this.props.fallback) {
        return this.props.fallback;
      }

      return <ErrorFallback error={this.state.error} onRetry={this.handleRetry} onReload={this.handleReload} />;
    }

    return this.props.children;
  }
}

/**
 * Default error fallback UI
 */
interface ErrorFallbackProps {
  error?: Error;
  onRetry: () => void;
  onReload: () => void;
}

function ErrorFallback({ error, onRetry, onReload }: ErrorFallbackProps) {
  return (
    <div className="min-h-screen flex items-center justify-center bg-gray-50 p-4">
      <div className="max-w-md w-full bg-white rounded-lg shadow-lg p-8 text-center">
        {/* Error Icon */}
        <div className="mx-auto w-16 h-16 bg-red-100 rounded-full flex items-center justify-center mb-6">
          <svg 
            className="w-8 h-8 text-red-600" 
            fill="none" 
            stroke="currentColor" 
            viewBox="0 0 24 24"
          >
            <path 
              strokeLinecap="round" 
              strokeLinejoin="round" 
              strokeWidth={2} 
              d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z" 
            />
          </svg>
        </div>

        <h1 className="text-2xl font-bold text-gray-900 mb-2">
          Jotain meni pieleen
        </h1>
        <p className="text-gray-600 mb-6">
          Sovelluksessa tapahtui odottamaton virhe. Olemme tallentaneet virhetiedot ja tutkimme asiaa.
        </p>

        {/* Error details (only in development) */}
        {import.meta.env.DEV && error && (
          <div className="mb-6 text-left">
            <div className="bg-gray-100 rounded p-4 overflow-auto max-h-48">
              <p className="text-sm font-mono text-red-600 mb-2">{error.message}</p>
              {error.stack && (
                <pre className="text-xs text-gray-600 whitespace-pre-wrap">{error.stack}</pre>
              )}
            </div>
          </div>
        )}

        {/* Action buttons */}
        <div className="flex flex-col sm:flex-row gap-3 justify-center">
          <Button onClick={onRetry} variant="primary">
            Yritä uudelleen
          </Button>
          <Button onClick={onReload} variant="outline">
            Päivitä sivu
          </Button>
        </div>

        {/* Support link */}
        <p className="mt-6 text-sm text-gray-500">
          Jos ongelma toistuu, ota yhteyttä tukeen:{' '}
          <a 
            href="mailto:tuki@kirjuri.fi" 
            className="text-blue-600 hover:underline"
          >
            tuki@kirjuri.fi
          </a>
        </p>
      </div>
    </div>
  );
}

/**
 * Smaller error boundary for specific sections (like cards or widgets)
 */
interface SectionErrorBoundaryProps {
  children: ReactNode;
  title?: string;
}

interface SectionErrorBoundaryState {
  hasError: boolean;
}

export class SectionErrorBoundary extends Component<
  SectionErrorBoundaryProps,
  SectionErrorBoundaryState
> {
  constructor(props: SectionErrorBoundaryProps) {
    super(props);
    this.state = { hasError: false };
  }

  static getDerivedStateFromError(): SectionErrorBoundaryState {
    return { hasError: true };
  }

  componentDidCatch(error: Error, errorInfo: ErrorInfo) {
    reportReactError(error, errorInfo.componentStack ?? undefined);
    console.error('[SectionErrorBoundary] Caught error:', error);
  }

  handleRetry = () => {
    this.setState({ hasError: false });
  };

  render() {
    if (this.state.hasError) {
      return (
        <div className="bg-red-50 border border-red-200 rounded-lg p-4">
          <div className="flex items-start gap-3">
            <svg 
              className="w-5 h-5 text-red-500 mt-0.5" 
              fill="none" 
              stroke="currentColor" 
              viewBox="0 0 24 24"
            >
              <path 
                strokeLinecap="round" 
                strokeLinejoin="round" 
                strokeWidth={2} 
                d="M12 8v4m0 4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" 
              />
            </svg>
            <div className="flex-1">
              <h4 className="text-sm font-medium text-red-800">
                {this.props.title || i18next.t('errorBoundary.title')}
              </h4>
              <p className="text-sm text-red-600 mt-1">
                {i18next.t('errorBoundary.description')}
              </p>
              <button
                onClick={this.handleRetry}
                className="mt-2 text-sm text-red-700 hover:text-red-800 font-medium"
              >
                {i18next.t('errorBoundary.retry')}
              </button>
            </div>
          </div>
        </div>
      );
    }

    return this.props.children;
  }
}
